import React from 'react';
import {
  Activity,
  Clock,
  Eye,
  Gauge,
  Settings,
  Target,
  ThumbsDown,
  ThumbsUp,
  User,
  Zap,
} from 'lucide-react';
import { Button } from '../../../../../../../components/ui/button';
import { Card, CardContent } from '../../../../../../../components/ui/card';
import { cn } from '../../../../../../../lib/utils';

export type PassViewLikeStatus = 'TRUE' | 'FALSE' | 'EX' | 'NULL';

export type WeldingPassSampleRow = {
  id: string;
  stationNumber: number | null;
  welderId: string;
  bugType: string;
  torch: string;
  passName: string;
  startTime: string;
  duration: string;
  autoincrement: string;
  status: PassViewLikeStatus;
  current: { min: string; avg: string; max: string };
  voltage: { min: string; avg: string; max: string };
  travelSpeed: { min: string; avg: string; max: string };
};

const SAMPLE_ROWS: WeldingPassSampleRow[] = [
  {
    id: '1',
    stationNumber: 1,
    welderId: 'W-1042',
    bugType: 'CW',
    torch: 'T1',
    passName: 'Root — Pass 1',
    startTime: '08:14:22',
    duration: '4m 12s',
    autoincrement: '18421',
    status: 'TRUE',
    current: { min: '156', avg: '162', max: '168' },
    voltage: { min: '23.8', avg: '24.8', max: '25.6' },
    travelSpeed: { min: '17.1', avg: '18.2', max: '19.0' },
  },
  {
    id: '2',
    stationNumber: 1,
    welderId: 'W-1042',
    bugType: 'CW',
    torch: 'T1',
    passName: 'Hot — Pass 2',
    startTime: '08:22:05',
    duration: '5m 01s',
    autoincrement: '18422',
    status: 'TRUE',
    current: { min: '172', avg: '178', max: '182' },
    voltage: { min: '25.2', avg: '26.1', max: '26.8' },
    travelSpeed: { min: '16.8', avg: '17.5', max: '18.4' },
  },
  {
    id: '3',
    stationNumber: 2,
    welderId: 'W-1042',
    bugType: 'CCW',
    torch: 'T2',
    passName: 'Fill — Pass 3',
    startTime: '08:31:40',
    duration: '6m 44s',
    autoincrement: '18423',
    status: 'EX',
    current: { min: '165', avg: '171', max: '179' },
    voltage: { min: '24.9', avg: '25.4', max: '26.2' },
    travelSpeed: { min: '13.2', avg: '14.1', max: '15.0' },
  },
  {
    id: '4',
    stationNumber: 2,
    welderId: 'W-1042',
    bugType: 'CCW',
    torch: 'T2',
    passName: 'Cap — Pass 4',
    startTime: '08:41:02',
    duration: '5m 28s',
    autoincrement: '18424',
    status: 'FALSE',
    current: { min: '182', avg: '188', max: '194' },
    voltage: { min: '26.8', avg: '27.9', max: '28.4' },
    travelSpeed: { min: '11.9', avg: '12.8', max: '13.6' },
  },
];

function StatusCell({ status }: { status: PassViewLikeStatus }) {
  if (status === 'TRUE') {
    return <ThumbsUp className="h-4 w-4 text-green-600 sm:h-5 sm:w-5" />;
  }
  if (status === 'FALSE') {
    return <ThumbsDown className="h-4 w-4 text-red-600 sm:h-5 sm:w-5" />;
  }
  if (status === 'EX') {
    return (
      <span className="flex h-4 w-4 items-center justify-center rounded-full bg-yellow-400 text-[10px] font-bold text-black sm:h-5 sm:w-5 sm:text-xs">
        !
      </span>
    );
  }
  return <span className="text-gray-600">-</span>;
}

function ParamHeader({
  icon: Icon,
  title,
  unit,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  unit: string;
}) {
  return (
    <th className="min-w-[130px] max-w-[200px] border-l border-gray-200 px-2">
      <div className="flex h-[72px] flex-col items-center justify-center py-1">
        <div className="flex items-center gap-1 text-center text-xs font-medium text-gray-600">
          <Icon className="h-4 w-4 shrink-0" />
          <span>
            {title}
            <br />
            <span className="font-normal text-gray-500">({unit})</span>
          </span>
        </div>
        <div className="mt-1 flex w-full justify-around border-t border-gray-100 pt-1 text-[10px] font-medium text-gray-500">
          <span>Min</span>
          <span className="text-gray-700">Avg</span>
          <span>Max</span>
        </div>
      </div>
    </th>
  );
}

function ParamCell({ min, avg, max }: { min: string; avg: string; max: string }) {
  return (
    <td className="h-[52px] min-w-0 border-l border-gray-200 px-2 py-2 text-xs">
      <div className="flex justify-around gap-1 font-mono">
        <span className="text-gray-500">{min}</span>
        <span className="font-semibold text-gray-900">{avg}</span>
        <span className="text-gray-500">{max}</span>
      </div>
    </td>
  );
}

const STICKY = {
  sl: 0,
  status: 72,
  station: 72 + 60,
  actions: 72 + 60 + 128,
} as const;

export interface WeldingPassSampleTableProps {
  titleHint?: string;
  /** Omit Card chrome — use inside tabs / modals. */
  embedded?: boolean;
  className?: string;
}

/** Pass view sample table — aligned with {@link PassViewTable}. */
export function WeldingPassSampleTable({ titleHint, embedded, className }: WeldingPassSampleTableProps) {
  const scroll = (
        <div
          className={
            embedded
              ? 'max-h-[min(420px,50vh)] overflow-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100'
              : 'max-h-[min(260px,38vh)] overflow-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100'
          }
        >
          <table className="w-full border-collapse" style={{ minWidth: 'max-content' }}>
            <thead className="sticky top-0 z-30 bg-gray-50 shadow-sm">
              <tr className="h-[72px] border-b bg-gray-50">
                <th
                  className="sticky left-0 z-20 min-w-[72px] bg-gray-50 px-2 text-center text-xs font-medium text-gray-600 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] sm:px-4"
                  style={{ left: STICKY.sl }}
                >
                  <div className="flex items-center justify-center gap-1">
                    <Target className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span className="hidden sm:inline">Sl.no</span>
                    <span className="sm:hidden">#</span>
                  </div>
                </th>
                <th
                  className="sticky z-20 w-[60px] min-w-[60px] max-w-[60px] bg-gray-50 px-2 text-center text-xs font-medium text-gray-600 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] sm:px-4"
                  style={{ left: STICKY.status }}
                >
                  <div className="flex items-center justify-center gap-1">
                    <Activity className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span className="hidden sm:inline">Status</span>
                  </div>
                </th>
                <th
                  className="sticky z-20 min-w-[100px] bg-gray-50 px-2 text-center text-xs font-medium text-gray-600 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] sm:px-4"
                  style={{ left: STICKY.station }}
                >
                  <div className="flex items-center justify-center gap-1">
                    <Target className="h-3 w-3 sm:h-4 sm:w-4" />
                    Station
                  </div>
                </th>
                <th
                  className="sticky z-20 min-w-[72px] bg-gray-50 px-2 text-center text-xs font-medium text-gray-600 sm:min-w-[80px] sm:px-4"
                  style={{ left: STICKY.actions, boxShadow: '4px 0 6px -2px rgba(0,0,0,0.12)' }}
                >
                  <div className="flex items-center justify-center gap-1">
                    <Settings className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span className="hidden sm:inline">Actions</span>
                  </div>
                </th>

                <th className="min-w-[100px] px-2 text-center text-xs font-medium text-gray-600 sm:px-4">
                  <div className="flex items-center justify-center gap-1">
                    <User className="h-3 w-3 sm:h-4 sm:w-4" />
                    Welder ID
                  </div>
                </th>
                <th className="min-w-[100px] px-2 text-center text-xs font-medium text-gray-600 sm:px-4">
                  <div className="flex items-center justify-center gap-1">
                    <Settings className="h-3 w-3 sm:h-4 sm:w-4" />
                    Bug Type
                  </div>
                </th>
                <th className="min-w-[80px] px-2 text-center text-xs font-medium text-gray-600 sm:px-4">
                  <div className="flex items-center justify-center gap-1">
                    <User className="h-3 w-3 sm:h-4 sm:w-4" />
                    Torch
                  </div>
                </th>
                <th className="min-w-[120px] px-2 text-center text-xs font-medium text-gray-600 sm:px-4">
                  <div className="flex items-center justify-center gap-1">
                    <Target className="h-3 w-3 sm:h-4 sm:w-4" />
                    Pass Name
                  </div>
                </th>
                <th className="min-w-[120px] px-2 text-center text-xs font-medium text-gray-600 sm:px-4">
                  <div className="flex items-center justify-center gap-1">
                    <Clock className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span className="hidden sm:inline">Weld Start Time</span>
                    <span className="sm:hidden">Start</span>
                  </div>
                </th>
                <th className="min-w-[72px] px-2 text-center text-xs font-medium text-gray-600 sm:px-4">
                  <div className="flex items-center justify-center gap-1">
                    <Gauge className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span className="hidden sm:inline">Weld Time</span>
                    <span className="sm:hidden">Time</span>
                  </div>
                </th>
                <th className="min-w-[100px] border-r border-gray-200 px-2 text-center text-xs font-medium text-gray-600 sm:px-4">
                  <div className="flex items-center justify-center gap-1">
                    <Target className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span className="hidden sm:inline">Auto Inc</span>
                    <span className="sm:hidden">Auto</span>
                  </div>
                </th>

                <ParamHeader icon={Zap} title="Current" unit="A" />
                <ParamHeader icon={Zap} title="Voltage" unit="V" />
                <ParamHeader icon={Gauge} title="Travel Speed" unit="cm/min" />
              </tr>
            </thead>
            <tbody>
              {SAMPLE_ROWS.map((pass, index) => (
                <tr
                  key={pass.id}
                  className={`h-[52px] ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${
                    index < SAMPLE_ROWS.length - 1 ? 'border-b' : ''
                  }`}
                >
                  <td
                    className={`sticky z-10 px-2 text-center text-xs font-medium text-gray-600 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] sm:px-4 ${
                      index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                    }`}
                    style={{ left: STICKY.sl }}
                  >
                    {index + 1}
                  </td>
                  <td
                    className={`sticky z-10 w-[60px] min-w-[60px] max-w-[60px] px-2 text-center shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] sm:px-4 ${
                      index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                    }`}
                    style={{ left: STICKY.status }}
                  >
                    <div className="inline-flex justify-center">
                      <StatusCell status={pass.status} />
                    </div>
                  </td>
                  <td
                    className={`sticky z-10 min-w-[100px] px-2 text-center text-xs text-gray-600 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] sm:px-4 ${
                      index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                    }`}
                    style={{ left: STICKY.station }}
                  >
                    {pass.stationNumber != null ? `Station ${pass.stationNumber}` : '-'}
                  </td>
                  <td
                    className={`sticky z-10 px-2 text-center sm:px-4 ${
                      index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                    }`}
                    style={{ left: STICKY.actions, boxShadow: '4px 0 6px -2px rgba(0,0,0,0.12)' }}
                  >
                    <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-gray-100" aria-label="View pass">
                      <Eye className="h-3 w-3 sm:h-4 sm:w-4" />
                    </Button>
                  </td>

                  <td className="px-2 text-center text-xs text-gray-600 sm:px-4">{pass.welderId}</td>
                  <td className="px-2 text-center text-xs text-gray-600 sm:px-4">{pass.bugType}</td>
                  <td className="px-2 text-center text-xs text-gray-600 sm:px-4">{pass.torch}</td>
                  <td className="px-2 text-center text-xs font-medium text-gray-800 sm:px-4">{pass.passName}</td>
                  <td className="px-2 text-center text-xs text-gray-600 sm:px-4">{pass.startTime}</td>
                  <td className="px-2 text-center text-xs text-gray-600 sm:px-4">{pass.duration}</td>
                  <td className="border-r border-gray-200 px-2 text-center text-xs text-gray-600 sm:px-4">
                    {pass.autoincrement}
                  </td>

                  <ParamCell min={pass.current.min} avg={pass.current.avg} max={pass.current.max} />
                  <ParamCell min={pass.voltage.min} avg={pass.voltage.avg} max={pass.voltage.max} />
                  <ParamCell min={pass.travelSpeed.min} avg={pass.travelSpeed.avg} max={pass.travelSpeed.max} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
  );

  if (embedded) {
    return <div className={cn('overflow-hidden rounded-md border border-gray-200 bg-white', className)}>{scroll}</div>;
  }

  return (
    <Card className="border border-gray-200">
      <div className="flex flex-col gap-0.5 border-b bg-gray-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="text-sm font-semibold text-gray-800">Pass view</h3>
        {titleHint ? <span className="text-xs font-medium text-gray-500">{titleHint}</span> : null}
      </div>
      <CardContent className="p-0">{scroll}</CardContent>
    </Card>
  );
}
